/**
 * One sentence of memo prose — the unit the whole memo is sourced in.
 *
 * A click opens the page or cell it came from in the viewer. Values of the
 * facts the sentence names are underlined and open THEIR own cell, which is
 * not always the sentence's page — "Revenue grew to $13.2mm" can be written
 * from a deck page while the number sits in the statements.
 *
 * Nothing about the source is shown on hover: a file name floating over the
 * prose reads as part of the memo and got in the way of reading it. Instead,
 * a sourced prose sentence ends in its source NUMBER (DOP-616), always
 * visible, so a reader can see what is sourced without hovering. The number
 * opens the source too, and `aria-describedby` still names it for a screen
 * reader.
 *
 * `link` says how the citation looks. A table cell is a discrete value and
 * carries the underline always, so a reader can see what opens; prose — and
 * a diligence question, which reads as prose — would be a wall of
 * underlines, so it underlines on hover. The figures a sentence names stay
 * underlined (DOP-556 review: a citation nobody can see is a citation nobody
 * clicks).
 *
 * A sentence a person put in that names a document (DOP-652) — typed by
 * them, or proposed by the memo chat and applied — is both: it opens its
 * page like any sourced sentence, and it reads in the person's colour so a
 * reader can see it is a change to what the run wrote.
 */

import React, { useId } from "react";

import type {
	DealScreeningFact,
	DealScreeningSentence,
	DealScreeningSource,
} from "../../types/dealScreening";
import { formatSourceLabel } from "./dealScreeningFormat";
import FormattedText from "./FormattedText";
import { fromStoredOffsets } from "./memoFormatModel";
import PersonNote from "./PersonNote";
import { segmentSentence } from "./memoModel";
import { useSourceNumber } from "./sourceNumbering";

export interface MemoSentenceProps {
	sentence: DealScreeningSentence;
	/** The sentence is a person's (DOP-580): no citation, and not flagged as unsourced. */
	edited?: boolean;
	/** "hover" for prose and questions (the default), "always" for a table cell. */
	link?: "hover" | "always";
	/** The facts `sentence.fact_keys` name, every period of each. */
	facts: DealScreeningFact[];
	onOpenSource: (source: DealScreeningSource) => void;
	/** Who wrote the sentence and when, for a note that is its source (DOP-637). */
	noteBy?: string | null;
	noteAt?: string | null;
}

const MemoSentence: React.FC<MemoSentenceProps> = ({
	sentence,
	facts,
	onOpenSource,
	link = "hover",
	edited = false,
	noteBy = null,
	noteAt = null,
}) => {
	const tooltipId = useId();
	const { source } = sentence;
	// The stored ranges count code points; the page counts UTF-16 (codex r2).
	const format = fromStoredOffsets(sentence.text, sentence.format);
	const number = useSourceNumber(link === "hover" && source?.kind !== "user" ? source?.anchor : null);
	// A person's words with a note on where they came from (DOP-637): the
	// note is the source, opened as a page is.
	if (source?.kind === "user") {
		return (
			<span className="relative">
				<PersonNote note={source.note ?? null} by={noteBy} at={noteAt} testId="sentence-note">
					<FormattedText text={sentence.text} format={format} />
				</PersonNote>{" "}
			</span>
		);
	}
	const open = () => {
		if (source) onOpenSource(source);
	};
	// Each segment's place in the sentence, for the formatting over it (DOP-641).
	const segments = segmentSentence(sentence.text, facts);
	const starts: number[] = [];
	segments.forEach((segment, index) =>
		starts.push(index === 0 ? 0 : starts[index - 1] + segments[index - 1].text.length),
	);

	return (
		<span className="group relative">
			<span
				role={source ? "link" : undefined}
				tabIndex={source ? 0 : undefined}
				aria-describedby={tooltipId}
				data-sentence-id={sentence.id}
				onClick={open}
				onKeyDown={(event) => {
					if (event.key === "Enter") open();
				}}
				data-edited={edited || undefined}
				className={`rounded-sm underline-offset-2 focus:outline-none ${
					source
						? `cursor-pointer ${link === "always" ? "underline" : "hover:underline"} ${
								edited
									? "text-[#5B3FA8] decoration-[#a48ff0] hover:bg-[#5B3FA8]/5 focus:bg-[#5B3FA8]/10"
									: "decoration-[#004C4B]/40 hover:bg-[#004C4B]/5 focus:bg-[#004C4B]/10"
							}`
						: edited
							? "text-[#5B3FA8]"
							: "bg-amber-50"
				}`}
			>
				{segments.map((segment, index) =>
					segment.fact?.source ? (
						<button
							// Segments are positional within an immutable sentence.
							// eslint-disable-next-line react/no-array-index-key
							key={index}
							type="button"
							onClick={(event) => {
								event.stopPropagation();
								if (segment.fact?.source) onOpenSource(segment.fact.source);
							}}
							className={`underline decoration-1 underline-offset-2 ${
								segment.fact.conflict === "open"
									? "text-[#b54708] decoration-[#b54708]"
									: "decoration-[#004C4B]"
							}`}
						>
							<FormattedText text={segment.text} format={format} offset={starts[index]} />
						</button>
					) : (
						// eslint-disable-next-line react/no-array-index-key
						<React.Fragment key={index}>
							<FormattedText text={segment.text} format={format} offset={starts[index]} />
						</React.Fragment>
					),
				)}
			</span>
			{source?.kind === "web" && (
				<span
					className="ml-0.5 font-sans text-[10px] text-[#004C4B]/70"
					aria-hidden
					data-testid="sentence-web-glyph"
				>
					↗
				</span>
			)}
			{source && number !== null && (
				<button
					type="button"
					onClick={open}
					aria-label={`Source ${number}: ${formatSourceLabel(source)}`}
					className={`ml-0.5 inline-flex h-4 min-w-[16px] -translate-y-0.5 items-center justify-center rounded px-1 align-middle font-sans text-[10px] font-semibold leading-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008782] ${
						edited ? "bg-[#f0ebff] text-[#5B3FA8] hover:bg-[#e4dcfd]" : "bg-[#E6F3F3] text-[#004C4B] hover:bg-[#CCE7E6]"
					}`}
					data-testid="source-chip"
				>
					{number}
				</button>
			)}{" "}
			<span id={tooltipId} className="sr-only">
				{source
					? `${formatSourceLabel(source)}${edited ? " (added in an edit)" : ""}`
					: edited
						? "Written by a person"
						: "No verified source"}
			</span>
		</span>
	);
};

export default React.memo(MemoSentence);
